import type { QueryClient } from '@tanstack/react-query';

import type { Lens } from '~/types/leLens';

import { editableLensDefinition, lensChangeLabel, lensDefinitionChanges } from './lensDefinitionChanges';
import { type LensProposal, globalLensProposalKey, lensProposalKey } from './lensProposal';

export type ReviewCandidateState = {
  purpose: 'review_proposal';
  status: 'current' | 'discarded' | 'submitted' | 'conflict' | 'expired';
  definition?: LensProposal['definition'];
  submittedProposalId?: string;
  message?: string;
  canSubmit: boolean;
  canDiscard: boolean;
  /** The exact server-derived summary that an omitted submit summary will use. */
  proposalSummary?: string;
};

export type ReviewCandidateAction = {
  candidateId: string;
  action: 'inspect' | 'discard' | 'submit';
  summary?: string;
};

export function canActOnReviewCandidate(candidate: ReviewCandidateState | undefined, action: 'submit' | 'discard') {
  if (!candidate) return false;
  return action === 'submit'
    ? candidate.status === 'current' && candidate.canSubmit === true && Boolean(candidate.proposalSummary?.trim())
    : (candidate.status === 'current' || candidate.status === 'conflict') && candidate.canDiscard === true;
}

export function customViewPresentationChanged(before: Lens['spec']['customView'], after: Lens['spec']['customView']) {
  return (
    Boolean(before) !== Boolean(after) ||
    (['html', 'css', 'javascript', 'height'] as const).some((part) => (before?.[part] ?? '') !== (after?.[part] ?? ''))
  );
}

export function reviewCandidateRequest(proposal: LensProposal, action: ReviewCandidateAction['action']) {
  if (proposal.purpose !== 'review_proposal' || !proposal.candidateId) {
    throw new Error('This is not an owner-review draft. Ask for the proposal again.');
  }
  return {
    path: `/api/reporting/le-lenses/${encodeURIComponent(proposal.lensId)}/proposal-candidates`,
    body: { candidateId: proposal.candidateId, action } as ReviewCandidateAction,
  };
}

/** Full definition changes, including card order and layout, without a data-query prerequisite. */
export function reviewDefinitionChanges(
  before: Pick<Lens, 'title' | 'description' | 'spec'>,
  after: NonNullable<LensProposal['definition']>,
) {
  return lensDefinitionChanges(editableLensDefinition(before), editableLensDefinition(after))
    .filter((change) => !/^spec\.customView\.(html|css|javascript)$/.test(change.field))
    .map((change) => ({ ...change, label: lensChangeLabel(change.field) }));
}

/** Finishing an older review must not erase a newer pending draft for this Lens. */
export function rememberReviewCandidate(client: QueryClient, proposal: LensProposal) {
  client.setQueryData(globalLensProposalKey(proposal), proposal);
  const latest = client.getQueryData<LensProposal>(lensProposalKey(proposal.lensId));
  if (!latest || latest.proposalId === proposal.proposalId || latest.createdAt <= proposal.createdAt) {
    client.setQueryData(lensProposalKey(proposal.lensId), proposal);
  }
}
